import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { Loader2, Trash2 } from "lucide-react";
import { BtnSimpleIcon } from "@/components/ui/button/btn-simple-icon";

import DispatchReasonPanel from "./reasonPanels/DispatchReasonPanel";
import LeaveReasonPanel from "./reasonPanels/LeaveReasonPanel";
import type { ResignationReason } from "./reasonPanels/reasonPanel.types";
import RetirementReasonPanel from "./reasonPanels/RetirementReasonPanel";
import TransferReasonPanel from "./reasonPanels/TransferReasonPanel";

interface NurseDateInfoSectionProps {
  joiningDate: string;
  startDate: string;
  resignationDate: string;
  targetGroupId: string;
  onStartDateChange: (value: string) => void;
  onTargetGroupIdChange: (value: string) => void;
  onJoiningDateChange: (value: string) => void;
  onResignationDateChange: (value: string) => void;
  resignationReason?: ResignationReason;
  resignationReasonMemo?: string | null;
  onResignationReasonChange: (value: ResignationReason) => void;
  onResignationReasonMemoChange: (value: string) => void;
  validationErrors?: {
    startDate?: boolean;
    endDate?: boolean;
    targetGroupId?: boolean;
  };
  /** 파견·병동이동 대상 병동 선택 시 제외 (간호사 소속 병동) */
  excludeTargetGroupId?: string | null;
  disabled?: boolean;
  /** disabled여도 기존 배정 '취소(삭제/초기화)'만 허용 — 전입자(target) inbound 취소용 */
  allowCancel?: boolean;
  /** 휴지통(삭제) 시 저장 없이 즉시 cancel — DispatchReasonPanel로 전달 */
  onImmediateCancel?: (assignmentId: number) => void;
  isImmediateCancelPending?: boolean;
  useOfficeGroupsForTarget?: boolean;
  officeIdForTargetGroups?: string | null;
  dispatchActivities?: Array<{
    assignmentId?: number;
    startDate: string;
    endDate: string;
    memo: string | null;
    targetGroupId: string;
  }>;
  onDispatchActivitiesChange?: (
    next: Array<{
      assignmentId?: number;
      startDate: string;
      endDate: string;
      memo: string | null;
      targetGroupId: string;
    }>
  ) => void;
  onImmediateDispatchSave?: (activity: {
    assignmentId?: number;
    startDate: string;
    endDate: string;
    memo: string | null;
    targetGroupId: string;
  }) => Promise<void>;
  isImmediateDispatchSaving?: boolean;
  /** inbound(활성 배정)에 해당 사유가 있으면 true — 탭에 배정 있음 표시 */
  reasonHasInboundData?: (reason: Exclude<ResignationReason, null>) => boolean;
  /** 단건 사유(휴직/퇴사/병동이동) 입력 폼 초기화 */
  onResetSingleReason?: (reason: "휴직" | "퇴사" | "병동이동") => void;
  /** 현재 편집 사유를 제외한 다른 사유의 점유 구간 */
  occupiedRanges?: Array<{
    startDate: string;
    endDate: string;
  }>;
  /** 휴직/퇴사/병동이동 날짜 선택 최소값(YYYY-MM-DD) */
  minSelectableDateForStatus?: string;
}

export default function NurseDateInfoSection({
  joiningDate,
  startDate,
  resignationDate,
  targetGroupId,
  onStartDateChange,
  onTargetGroupIdChange,
  onResignationDateChange,
  resignationReason,
  resignationReasonMemo,
  onResignationReasonChange,
  onResignationReasonMemoChange,
  validationErrors,
  excludeTargetGroupId,
  disabled,
  allowCancel,
  onImmediateCancel,
  isImmediateCancelPending = false,
  useOfficeGroupsForTarget,
  officeIdForTargetGroups,
  dispatchActivities,
  onDispatchActivitiesChange,
  onImmediateDispatchSave,
  isImmediateDispatchSaving,
  reasonHasInboundData,
  onResetSingleReason,
  occupiedRanges,
  minSelectableDateForStatus,
}: NurseDateInfoSectionProps) {
  const minDateForNonDispatch = minSelectableDateForStatus || joiningDate;
  const reasonTabs: Array<{
    value: Exclude<ResignationReason, null>;
    label: string;
  }> = [
    { value: "병동이동", label: "병동이동" },
    { value: "파견", label: "파견" },
    { value: "휴직", label: "휴직" },
    { value: "퇴사", label: "퇴사" },
  ];

  const activeReason = resignationReason ?? null;
  const isResettableReason =
    activeReason === "휴직" ||
    activeReason === "퇴사" ||
    activeReason === "병동이동";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 items-center">
        <label className="text-[15px] font-semibold text-text-light-gray font-pretendard">
          근무 상태
        </label>
        <span className="text-xs text-text-light-gray font-pretendard font-light -ml-[7px]">
          *근무 상태에 따라 근무표에 반영
        </span>
      </div>
      <div className="grid h-auto w-full grid-cols-4 gap-1">
        {reasonTabs.map((reasonTab) => {
          const isSelected = activeReason === reasonTab.value;
          const hasInbound = reasonHasInboundData?.(reasonTab.value) ?? false;

          return (
            <Button
              key={reasonTab.value}
              type="button"
              variant="outline"
              title={
                hasInbound
                  ? "활성 배정(inbound)에 이 사유가 있습니다"
                  : undefined
              }
              aria-pressed={isSelected}
              onClick={() =>
                onResignationReasonChange(isSelected ? null : reasonTab.value)
              }
              className={cn(
                "py-2 h-auto rounded-sm relative border text-sm font-semibold transition-colors font-pretendard",
                "border border-border bg-transparent! text-muted-foreground/60 hover:text-sig/80 hover:border-sig/80",
                isSelected && "text-sig border-sig bg-[#F8FBFF] shadow-none"
              )}
            >
              {hasInbound && (
                <span
                  className="size-1 shrink-0 rounded-full bg-sig/50 absolute left-[4px] top-[4px]"
                  aria-hidden
                />
              )}

              <span className="inline-flex items-center justify-center gap-1 truncate">
                {reasonTab.label}
              </span>
            </Button>
          );
        })}
      </div>

      {isResettableReason && (
        <div className="flex justify-between pt-4 font-semibold border-b ">
          <span className="text-base font-pretendard text-muted-foreground">
            {activeReason} 설정
          </span>
          {(!disabled || allowCancel) && (
            <BtnSimpleIcon
              onClick={() => onResetSingleReason?.(activeReason)}
              disabled={isImmediateCancelPending}
              tone="destructive"
              className="p-1"
              title="입력값을 초기화합니다."
              aria-label="근무상태 입력 초기화"
              icon={
                isImmediateCancelPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )
              }
            />
          )}
        </div>
      )}

      {activeReason === "휴직" && (
        <div>
          <LeaveReasonPanel
            joiningDate={minDateForNonDispatch}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            isStartDateError={validationErrors?.startDate}
            resignationDate={resignationDate}
            onResignationDateChange={onResignationDateChange}
            isEndDateError={validationErrors?.endDate}
            targetGroupId={targetGroupId}
            onTargetGroupIdChange={onTargetGroupIdChange}
            isTargetGroupError={validationErrors?.targetGroupId}
            memo={resignationReasonMemo}
            onMemoChange={onResignationReasonMemoChange}
            disabled={disabled}
            occupiedRanges={occupiedRanges}
          />
        </div>
      )}
      {activeReason === "퇴사" && (
        <div>
          <RetirementReasonPanel
            joiningDate={minDateForNonDispatch}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            isStartDateError={validationErrors?.startDate}
            resignationDate={resignationDate}
            onResignationDateChange={onResignationDateChange}
            isEndDateError={validationErrors?.endDate}
            targetGroupId={targetGroupId}
            onTargetGroupIdChange={onTargetGroupIdChange}
            isTargetGroupError={validationErrors?.targetGroupId}
            memo={resignationReasonMemo}
            onMemoChange={onResignationReasonMemoChange}
            disabled={disabled}
            occupiedRanges={occupiedRanges}
          />
        </div>
      )}
      {activeReason === "파견" && (
        <div>
          <DispatchReasonPanel
            joiningDate={joiningDate}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            isStartDateError={validationErrors?.startDate}
            resignationDate={resignationDate}
            onResignationDateChange={onResignationDateChange}
            isEndDateError={validationErrors?.endDate}
            targetGroupId={targetGroupId}
            onTargetGroupIdChange={onTargetGroupIdChange}
            isTargetGroupError={validationErrors?.targetGroupId}
            memo={resignationReasonMemo}
            onMemoChange={onResignationReasonMemoChange}
            excludeTargetGroupId={excludeTargetGroupId}
            disabled={disabled}
            allowCancel={allowCancel}
            onImmediateCancel={onImmediateCancel}
            isImmediateCancelPending={isImmediateCancelPending}
            useOfficeGroupsForTarget={useOfficeGroupsForTarget}
            officeIdForTargetGroups={officeIdForTargetGroups}
            dispatchActivities={dispatchActivities}
            onDispatchActivitiesChange={onDispatchActivitiesChange}
            onImmediateDispatchSave={onImmediateDispatchSave}
            isImmediateDispatchSaving={isImmediateDispatchSaving}
            occupiedRanges={occupiedRanges}
          />
        </div>
      )}
      {activeReason === "병동이동" && (
        <div>
          <TransferReasonPanel
            joiningDate={minDateForNonDispatch}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            isStartDateError={validationErrors?.startDate}
            resignationDate={resignationDate}
            onResignationDateChange={onResignationDateChange}
            isEndDateError={validationErrors?.endDate}
            targetGroupId={targetGroupId}
            onTargetGroupIdChange={onTargetGroupIdChange}
            isTargetGroupError={validationErrors?.targetGroupId}
            memo={resignationReasonMemo}
            onMemoChange={onResignationReasonMemoChange}
            excludeTargetGroupId={excludeTargetGroupId}
            disabled={disabled}
            useOfficeGroupsForTarget={useOfficeGroupsForTarget}
            officeIdForTargetGroups={officeIdForTargetGroups}
            occupiedRanges={occupiedRanges}
          />
        </div>
      )}
    </div>
  );
}
